import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { listAdminTimeline } from "@/lib/shifts.functions";
import { getIsAdmin } from "@/lib/payments.functions";
import { ReserveHeader } from "@/components/ReserveHeader";

export const Route = createFileRoute("/_authenticated/admin/timeline")({
  head: () => ({ meta: [{ title: "Shift Timeline — Admin" }] }),
  component: AdminTimeline,
});

type Event = {
  id: string;
  shift_id: string;
  actor_user_id: string | null;
  actor_role: string;
  event_type: string;
  details: Record<string, unknown>;
  created_at: string;
};

const LABEL: Record<string, string> = {
  shift_rescheduled: "Rescheduled",
  shift_cancelled: "Cancelled by venue",
  bartender_dropped_out: "Bartender dropped out",
};

function AdminTimeline() {
  const navigate = useNavigate();
  const checkAdmin = useServerFn(getIsAdmin);
  const load = useServerFn(listAdminTimeline);
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const a = await checkAdmin();
      if (!a.isAdmin) { navigate({ to: "/dashboard", replace: true }); return; }
      try { const r = await load(); setEvents(r.events as Event[]); }
      catch (e) { toast.error(e instanceof Error ? e.message : "Failed to load timeline."); }
      finally { setLoading(false); }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <ReserveHeader role="admin" />
      <main className="mx-auto max-w-6xl px-6 py-10">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <p className="tracking-wide-luxury text-accent">Admin Timeline</p>
            <h1 className="mt-3 font-display text-3xl">Recent shift activity</h1>
          </div>
          <div className="flex gap-2">
            <Link to="/admin" className="border border-border px-4 py-2 text-xs tracking-wide-luxury hover:border-accent">Console</Link>
            <Link to="/admin/disputes" className="border border-border px-4 py-2 text-xs tracking-wide-luxury hover:border-accent">Disputes</Link>
            <Link to="/admin/analytics" className="border border-border px-4 py-2 text-xs tracking-wide-luxury hover:border-accent">Analytics</Link>
          </div>
        </div>
        <span className="gold-divider mt-4" />

        {loading ? (
          <p className="mt-12 text-center text-sm text-muted-foreground">Loading…</p>
        ) : events.length === 0 ? (
          <p className="mt-12 text-center text-sm text-muted-foreground">No activity recorded yet.</p>
        ) : (
          <ol className="mt-8 space-y-3">
            {events.map((e) => (
              <li key={e.id} className="border border-border bg-card p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-xs tracking-wide-luxury text-accent uppercase">
                      {LABEL[e.event_type] ?? e.event_type} · {e.actor_role}
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">{new Date(e.created_at).toLocaleString()}</p>
                    <pre className="mt-2 text-xs whitespace-pre-wrap font-mono text-foreground/80">
                      {JSON.stringify(e.details, null, 2)}
                    </pre>
                  </div>
                  <Link to="/shift/$id/notes" params={{ id: e.shift_id }}
                    className="border border-border px-3 py-2 text-xs tracking-wide-luxury hover:border-accent">
                    Open shift
                  </Link>
                </div>
              </li>
            ))}
          </ol>
        )}
      </main>
    </div>
  );
}
